if (import.meta.hot) {
  import.meta.hot.accept();
}

import './sidebar.scss';

const PANEL_SELECTOR = '[data-sidebar]';
const CLOSE_SELECTOR = '[data-sidebar-close]';
const SCROLL_LOCK_CLASS = 'is-scroll-locked';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const sidebars = new Map();

class Sidebar {
  constructor(panel) {
    this.panel = panel;
    this.id = panel.id;
    this.lastTrigger = null;
    this.triggers = Array.from(document.querySelectorAll(`button[aria-controls="${this.id}"]`));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(trigger);
      });
    });

    panel.querySelectorAll(CLOSE_SELECTOR).forEach((button) => {
      button.addEventListener('click', () => this.close(true));
    });

    panel.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close(true);
    });

    panel.addEventListener('click', (event) => {
      if (event.target === panel) {
        this.close(true);
      }
    });

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => !el.closest('[hidden]'));

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  isOpen() {
    return Boolean(this.panel.open);
  }

  toggle(trigger = null) {
    if (this.isOpen()) {
      this.close(true);
    } else {
      this.open(trigger);
    }
  }

  open(trigger = null) {
    if (this.isOpen()) {
      return;
    }

    document.dispatchEvent(new CustomEvent('sidebar:beforeopen', {
      detail: { id: this.id, panel: this.panel, instance: this },
    }));

    closeAllSidebars({ except: this });

    this.lastTrigger = trigger;
    this.panel.hidden = false;
    if (typeof this.panel.showModal === 'function') {
      this.panel.showModal();
    } else {
      this.panel.setAttribute('open', '');
    }

    this.panel.querySelector(FOCUSABLE_SELECTOR)?.focus();

    this.syncTriggerState(true);
    syncScrollLock();

    document.dispatchEvent(new CustomEvent('sidebar:open', {
      detail: { id: this.id, panel: this.panel, instance: this },
    }));
  }

  close(restoreFocus = false) {
    if (!this.isOpen() && this.panel.hidden) {
      return;
    }

    if (this.panel.open) {
      this.panel.close();
    }
    this.panel.hidden = true;
    this.syncTriggerState(false);
    syncScrollLock();

    if (restoreFocus && this.lastTrigger) {
      this.lastTrigger.focus({ preventScroll: true });
    }
    this.lastTrigger = null;

    document.dispatchEvent(new CustomEvent('sidebar:close', {
      detail: { id: this.id, panel: this.panel, instance: this },
    }));
  }

  syncTriggerState(expanded) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }
}

function closeAllSidebars({ except = null, restoreFocus = false } = {}) {
  sidebars.forEach((sidebar) => {
    if (sidebar !== except) {
      sidebar.close(restoreFocus);
    }
  });
}

function anySidebarOpen() {
  return Array.from(sidebars.values()).some((sidebar) => sidebar.isOpen());
}

function syncScrollLock() {
  document.documentElement.classList.toggle(SCROLL_LOCK_CLASS, anySidebarOpen());
  document.body.classList.toggle(SCROLL_LOCK_CLASS, anySidebarOpen());
}

document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
  if (!panel.id) {
    return;
  }
  sidebars.set(panel.id, new Sidebar(panel));
});

window.altanaSidebar = {
  closeAll: closeAllSidebars,
  getById: (id) => sidebars.get(id),
  anyOpen: anySidebarOpen,
};
